import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import { memo } from "react";
import { useSelector } from "react-redux";
import { COLLECTIONS_PLAN_TYPE } from "../../consts/planTypes";
import { palette } from "../../theme/palette";

import {
  getMatrixProfileType,
  volumeDataSelector,
} from "../../reducers/matrixProfile/selectors";
import { selectedCollectionTabSelector } from "../../reducers/ownApp/selectors";
import {
  getWeight,
  numberWithCommas,
  routeHasTerritory,
} from "../../utils/routes";
import { getTotalDuty } from "../../utils/drivers";

import { DepartIcon, LeafIcon, PreciseIcon, ReturnIcon } from "../Icons";
import BaseCard from "./BaseCard";
import { ExtendedCardInfo } from "./ExtendedCardInfo";
import { TextIconInfo } from "./TextIconInfo";
import { TimeInfo } from "./TimeInfo";
import { View } from "../View";

const useStyles = makeStyles({
  container: {
    color: "white",
    marginTop: "2px",
  },
  inline: {
    display: "flex",
    flexDirection: "row",
    marginTop: "4px",
  },
  text: {
    fontSize: "12px",
    color: palette.typography.white.high,
    lineHeight: "16px",
  },
  space: {
    width: "12px",
  },
  vehicleRow: {
    display: "flex",
    alignItems: "center",
  },
  leftSpace: {
    marginLeft: "5px",
  },
  warningIcon: {
    width: "12px",
    height: "12px",
    fill: "#FFD500",
  },
  parcelIcon: {
    width: "11px",
    height: "12px",
    fill: palette.typography.white.medium,
  },
  collectionIcon: {
    width: "12px",
    height: "12px",
    fill: palette.typography.white.medium,
  },
  stopsIcon: {
    width: "12px",
    height: "12px",
    fill: palette.typography.white.medium,
    marginBottom: "-2px",
  },
  clockIcon: {
    width: "12px",
    height: "12px",
    fill: "#51D61F",
  },
  spaceBetween: {
    display: "flex",
    justifyContent: "space-between",
  },
});

export const CompactCard = memo(
  ({
    route,
    driver,
    vehicle,
    selected,
    onClick,
    onEdit,
    onClear,
    onEditDriverClick,
    scrollOffset,
  }) => {
    const styles = useStyles();
    const planType = useSelector(getMatrixProfileType);
    const collectionType = useSelector(selectedCollectionTabSelector);
    const volumeData = useSelector(volumeDataSelector);
    const hasTerritory = routeHasTerritory(route, planType, collectionType);

    return (
      <div>
        <BaseCard
          route={route}
          driver={driver}
          spot={route.colour}
          selected={selected}
          onClick={onClick}
          onEdit={onEdit}
          onClear={onClear}
          onEditDriverClick={onEditDriverClick}
          scrollOffset={scrollOffset}
        >
          <View className={styles.container}>
            <View className={clsx(styles.text, styles.inline)}>
              <TimeInfo time={[driver?.dutyStartTime, driver?.dutyEndTime]} />
              <View className={styles.space} />
              <TimeInfo
                time={driver?.plannedDepartTime}
                icon={<DepartIcon />}
              />
              <View className={styles.space} />
              <TimeInfo time={getTotalDuty(driver)} icon={<PreciseIcon />} />
              <View className={styles.space} />
              <TimeInfo
                time={driver?.plannedReturnTime}
                icon={<ReturnIcon />}
              />
            </View>

            <ExtendedCardInfo route={route} />

            <TextIconInfo
              keyName={"Vehicle"}
              value={vehicle?.description}
              icon={vehicle?.electricVehicle && <LeafIcon />}
            />

            {volumeData && planType !== COLLECTIONS_PLAN_TYPE && (
              <TextIconInfo
                keyName={"Weight"}
                value={`${numberWithCommas(getWeight(route))} kg`}
              />
            )}

            {hasTerritory && route.plannedReadyTime && (
              <TextIconInfo
                keyName={"Forecast Ready Time"}
                value={route.plannedReadyTimeAsString}
              />
            )}
            {hasTerritory && !route.plannedReadyTime && (
              <TextIconInfo keyName={"Final Wave Route"} />
            )}
          </View>
        </BaseCard>
      </div>
    );
  }
);
